import type { GbTier } from "@/lib/pricing-utils";

export const PRICING_VERIFIED_DATE = "July 10, 2026";

export const DECODO_URL = "https://decodo.com";
export const DECODO_MOBILE_URL = "https://decodo.com/proxies/mobile";
export const DECODO_MOBILE_PRICING_URL = "https://decodo.com/proxies/mobile/pricing";
export const DECODO_RESIDENTIAL_URL = "https://decodo.com/proxies/residential-proxies";
export const DECODO_RESIDENTIAL_PRICING_URL =
  "https://decodo.com/proxies/residential-proxies/pricing";
export const DECODO_ISP_PRICING_URL = "https://decodo.com/proxies/isp-proxies/pricing";
export const DECODO_DATACENTER_URL = "https://decodo.com/proxies/cheap";
export const DECODO_PRODUCTS_URL = "https://decodo.com/products";
export const DECODO_LICENSE_URL = "https://decodo.com/legal/license-agreement";
export const DECODO_SMARTPROXY_REBRAND_URL = "https://decodo.com/smartproxy";
export const DECODO_TRUSTPILOT_URL = "https://www.trustpilot.com/review/decodo.com";
export const DECODO_G2_URL = "https://www.g2.com/products/decodo-formerly-smartproxy/reviews";

export type ProxyProduct = "mobile" | "residential";

export interface DecodoPlan {
  name: string;
  gb: number;
  monthlyPrice: number;
  perGb: number;
}

export const DECODO_MOBILE_PLANS: DecodoPlan[] = [
  { name: "2 GB", gb: 2, monthlyPrice: 7.5, perGb: 3.75 },
  { name: "8 GB", gb: 8, monthlyPrice: 28, perGb: 3.5 },
  { name: "25 GB", gb: 25, monthlyPrice: 81.3, perGb: 3.25 },
  { name: "50 GB", gb: 50, monthlyPrice: 150, perGb: 3.0 },
  { name: "100 GB", gb: 100, monthlyPrice: 275, perGb: 2.75 },
];

export const DECODO_RESIDENTIAL_PLANS: DecodoPlan[] = [
  { name: "3 GB", gb: 3, monthlyPrice: 11.25, perGb: 3.75 },
  { name: "10 GB", gb: 10, monthlyPrice: 35, perGb: 3.5 },
  { name: "25 GB", gb: 25, monthlyPrice: 81.25, perGb: 3.25 },
  { name: "50 GB", gb: 50, monthlyPrice: 150, perGb: 3.0 },
];

export const PROXIDIZE_MOBILE_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 2, totalPrice: 50 },
  { gb: 50, monthlyPPG: 2, totalPrice: 100 },
  { gb: 100, monthlyPPG: 2, totalPrice: 200 },
  { gb: 200, monthlyPPG: 2, totalPrice: 400 },
  { gb: 500, monthlyPPG: 2, totalPrice: 1000 },
  { gb: 1000, monthlyPPG: 2, totalPrice: 2000 },
  { gb: 2000, monthlyPPG: 2, totalPrice: 4000 },
  { gb: 5000, monthlyPPG: 2, totalPrice: 10000 },
  { gb: 10000, monthlyPPG: 2, totalPrice: 20000 },
];

export const PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 1, totalPrice: 25 },
  { gb: 50, monthlyPPG: 1, totalPrice: 50 },
  { gb: 100, monthlyPPG: 1, totalPrice: 100 },
  { gb: 200, monthlyPPG: 1, totalPrice: 200 },
  { gb: 500, monthlyPPG: 1, totalPrice: 500 },
  { gb: 1000, monthlyPPG: 1, totalPrice: 1000 },
  { gb: 2000, monthlyPPG: 0.95, totalPrice: 1900 },
  { gb: 5000, monthlyPPG: 0.95, totalPrice: 4750 },
  { gb: 10000, monthlyPPG: 0.9, totalPrice: 9000 },
];

export interface DecodoCost {
  cost: number;
  planLabel: string;
  extrapolated: boolean;
}

export function decodoMonthlyCost(product: ProxyProduct, gb: number): DecodoCost {
  const plans = product === "mobile" ? DECODO_MOBILE_PLANS : DECODO_RESIDENTIAL_PLANS;
  const plan = plans.find((p) => p.gb >= gb);
  if (plan)
    return {
      cost: plan.monthlyPrice,
      planLabel: `${plan.name} plan`,
      extrapolated: false,
    };
  const top = plans[plans.length - 1];
  const cost = Math.round((top.monthlyPrice + (gb - top.gb) * top.perGb) * 100) / 100;
  return { cost, planLabel: `${top.name} plan + top-up`, extrapolated: true };
}
